import { CoreError } from './errors'
import type { BuildContext, DocLineInput, NumberingInput } from './documents'
import type { Paisa } from './money'
import { formatDocNumber } from './numbering'
import type { Milli } from './qty'
import { mulDivRound } from './rounding'
import type {
  SalesOrderKind,
  SalesOrderLineRow,
  SalesOrderRow,
  SalesOrderStatus,
  SyncOp,
  SyncRowBase,
} from './rows'
import { computeDocument, type RoundOffMode } from './totals'

declare const crypto: { randomUUID(): string }

// Quotations and sales orders (sales_orders.kind). They are paperwork: no
// builder here writes a stock movement. Stock leaves on the invoice made from
// one (buildInvoice with salesOrderId), and how much of an order has been
// invoiced is summed from those invoice lines, never stored.
//
// A document is numbered when it leaves draft and frozen from then on; the
// server refuses anything but a status move (see the sales_orders migration).

function base(ctx: BuildContext, id?: string): SyncRowBase {
  return {
    id: id ?? (ctx.newId ?? (() => crypto.randomUUID()))(),
    tenant_id: ctx.tenantId,
    created_at: ctx.now,
    updated_at: ctx.now,
    deleted_at: null,
  }
}

function op(table: string, row: object): SyncOp {
  return { table, op: 'upsert', row: { ...row } }
}

/** The status moves the server allows, per kind. */
const MOVES: Record<SalesOrderKind, Partial<Record<SalesOrderStatus, SalesOrderStatus[]>>> = {
  quotation: {
    draft: ['sent', 'accepted'],
    sent: ['accepted', 'declined', 'cancelled'],
    accepted: ['cancelled'],
  },
  order: {
    draft: ['confirmed'],
    confirmed: ['closed', 'cancelled'],
  },
}

export function canMoveSalesOrder(
  order: Pick<SalesOrderRow, 'kind' | 'status'>,
  to: SalesOrderStatus,
): boolean {
  return MOVES[order.kind][order.status]?.includes(to) ?? false
}

function numberFields(numbering: NumberingInput | undefined, numbered: boolean) {
  if (!numbered)
    return { counter_id: numbering?.counter.id ?? null, fiscal_year: null, seq: null, number: null }
  if (!numbering) throw new CoreError('numbering_required', 'a numbered document needs a counter')
  const { counter, prefix, fiscalYear, seq } = numbering
  if (counter.kind === 'web') {
    return { counter_id: counter.id, fiscal_year: fiscalYear, seq: null, number: null }
  }
  if (seq === undefined)
    throw new CoreError('numbering_required', 'device counters need the next seq')
  return {
    counter_id: counter.id,
    fiscal_year: fiscalYear,
    seq,
    number: formatDocNumber(prefix, counter.code, fiscalYear, seq),
  }
}

export interface BuildSalesOrderInput {
  id?: string
  kind: SalesOrderKind
  /** A draft, or straight to the first numbered status of its kind. */
  status: 'draft' | 'sent' | 'accepted' | 'confirmed'
  docDate: string
  /** Quotations: the last day the prices hold. */
  validUntil?: string | null
  /** Orders: when the customer expects the goods. */
  expectedDate?: string | null
  customer?: {
    id?: string | null
    name: string
    pan?: string | null
    address?: string | null
  } | null
  locationId?: string | null
  currency?: string
  lines: readonly DocLineInput[]
  documentDiscountPaisa?: Paisa
  roundOff?: RoundOffMode
  notes?: string | null
  terms?: string | null
  /** The quotation an order came from, or the document this one revises. */
  sourceId?: string | null
  /** Needed unless the document stays a draft. */
  numbering?: NumberingInput
}

export interface BuiltSalesOrder {
  order: SalesOrderRow
  lines: SalesOrderLineRow[]
  ops: SyncOp[]
}

/**
 * A quotation or an order with its lines, as one tx. Saving a draft again with
 * the same `id` replaces it: pass the stored lines as `replaces` so the ones
 * that are gone are deleted in the same tx.
 */
export function buildSalesOrder(
  ctx: BuildContext,
  input: BuildSalesOrderInput,
  replaces: readonly Pick<SalesOrderLineRow, 'id'>[] = [],
): BuiltSalesOrder {
  const what = input.kind === 'quotation' ? 'a quotation' : 'an order'
  if (input.lines.length === 0)
    throw new CoreError('invalid_order', `${what} needs at least one line`)
  if (
    input.status !== 'draft' &&
    !canMoveSalesOrder({ kind: input.kind, status: 'draft' }, input.status)
  )
    throw new CoreError('invalid_transition', `${what} cannot start as ${input.status}`)
  if (input.validUntil && input.validUntil < input.docDate)
    throw new CoreError('invalid_input', 'valid until cannot be before the date')
  for (const l of input.lines) {
    if (!l.description.trim()) throw new CoreError('invalid_line', 'a line needs a description')
  }
  const numbered = input.status !== 'draft'
  const doc = computeDocument(input.lines, {
    documentDiscountPaisa: input.documentDiscountPaisa ?? 0,
    roundOff: input.roundOff ?? 'none',
  })

  const order: SalesOrderRow = {
    ...base(ctx, input.id),
    kind: input.kind,
    status: input.status,
    ...numberFields(input.numbering, numbered),
    doc_date: input.docDate,
    valid_until: input.kind === 'quotation' ? (input.validUntil ?? null) : null,
    expected_date: input.kind === 'order' ? (input.expectedDate ?? null) : null,
    customer_id: input.customer?.id ?? null,
    customer_name: input.customer?.name ?? null,
    customer_pan: input.customer?.pan ?? null,
    customer_address: input.customer?.address ?? null,
    location_id: input.locationId ?? null,
    currency: input.currency ?? 'NPR',
    subtotal_paisa: doc.totals.subtotalPaisa,
    discount_paisa: doc.totals.discountPaisa,
    taxable_paisa: doc.totals.taxablePaisa,
    tax_paisa: doc.totals.taxPaisa,
    round_off_paisa: doc.totals.roundOffPaisa,
    total_paisa: doc.totals.totalPaisa,
    notes: input.notes?.trim() || null,
    terms: input.terms?.trim() || null,
    source_id: input.sourceId ?? null,
    closed_at: null,
    close_reason: null,
  }

  const lines: SalesOrderLineRow[] = input.lines.map((l, i) => {
    const amounts = doc.lines[i]!
    return {
      ...base(ctx),
      sales_order_id: order.id,
      line_no: i + 1,
      product_id: l.productId ?? null,
      description: l.description.trim(),
      qty_milli: l.qtyMilli,
      unit_price_paisa: l.unitPricePaisa,
      price_includes_tax: l.priceIncludesTax,
      gross_paisa: amounts.grossPaisa,
      discount_paisa: amounts.discountPaisa,
      tax_rate_id: l.taxRateId ?? null,
      tax_rate_bp: l.taxRateBp,
      taxable_paisa: amounts.taxablePaisa,
      tax_paisa: amounts.taxPaisa,
      total_paisa: amounts.totalPaisa,
    }
  })

  return {
    order,
    lines,
    ops: [
      op('sales_orders', order),
      // Old lines go first: the server applies ops of one table in this order.
      ...replaces.map((r): SyncOp => ({
        table: 'sales_order_lines',
        op: 'delete',
        row: { id: r.id },
      })),
      ...lines.map((r) => op('sales_order_lines', r)),
    ],
  }
}

/**
 * Moves a document to another status. Leaving draft numbers it (pass
 * `numbering`); closing or cancelling needs a reason.
 */
export function setSalesOrderStatus(
  ctx: BuildContext,
  order: SalesOrderRow,
  to: SalesOrderStatus,
  options: { reason?: string; numbering?: NumberingInput } = {},
): { order: SalesOrderRow; ops: SyncOp[] } {
  if (!canMoveSalesOrder(order, to))
    throw new CoreError('invalid_transition', `a ${order.status} ${order.kind} cannot become ${to}`)
  const closing = to === 'closed' || to === 'cancelled'
  const reason = options.reason?.trim() ?? ''
  if (closing && !reason) throw new CoreError('invalid_input', 'a reason is required')
  const next: SalesOrderRow = {
    ...order,
    status: to,
    ...(order.status === 'draft' ? numberFields(options.numbering, true) : {}),
    closed_at: closing ? ctx.now : null,
    close_reason: closing ? reason : null,
    updated_at: ctx.now,
  }
  return { order: next, ops: [op('sales_orders', next)] }
}

/**
 * A correction to a document that has left draft: a new document that points
 * back at it, and the old one retired in the same tx (a quotation is
 * cancelled, an order closed). A declined quotation stays as it is.
 */
export function reviseSalesOrder(
  ctx: BuildContext,
  original: SalesOrderRow,
  input: Omit<BuildSalesOrderInput, 'kind' | 'sourceId'>,
): BuiltSalesOrder & { original: SalesOrderRow } {
  if (original.status === 'draft')
    throw new CoreError('invalid_transition', 'a draft is edited, not revised')
  const built = buildSalesOrder(ctx, { ...input, kind: original.kind, sourceId: original.id })
  const retire: SalesOrderStatus = original.kind === 'quotation' ? 'cancelled' : 'closed'
  if (!canMoveSalesOrder(original, retire)) return { ...built, original }
  const retired = setSalesOrderStatus(ctx, original, retire, { reason: 'Revised' })
  return { ...built, original: retired.order, ops: [...retired.ops, ...built.ops] }
}

/**
 * An order for what a quotation offered: the same lines at the agreed prices,
 * pointing back at the quotation, which is marked accepted in the same tx.
 */
export function orderFromQuotation(
  ctx: BuildContext,
  quotation: SalesOrderRow,
  lines: readonly SalesOrderLineRow[],
  input: Pick<
    BuildSalesOrderInput,
    'status' | 'docDate' | 'expectedDate' | 'locationId' | 'roundOff' | 'numbering'
  >,
): BuiltSalesOrder & { quotation: SalesOrderRow } {
  if (quotation.kind !== 'quotation' || !['sent', 'accepted'].includes(quotation.status))
    throw new CoreError('invalid_transition', 'only an open quotation can become an order')
  const built = buildSalesOrder(ctx, {
    ...input,
    kind: 'order',
    sourceId: quotation.id,
    customer: quotation.customer_name
      ? {
          id: quotation.customer_id,
          name: quotation.customer_name,
          pan: quotation.customer_pan,
          address: quotation.customer_address,
        }
      : null,
    currency: quotation.currency,
    notes: quotation.notes,
    terms: quotation.terms,
    lines: invoiceLinesFromOrder(lines).map(({ salesOrderLineId: _line, ...l }) => l),
  })
  if (quotation.status !== 'sent') return { ...built, quotation }
  const accepted = setSalesOrderStatus(ctx, quotation, 'accepted')
  return { ...built, quotation: accepted.order, ops: [...accepted.ops, ...built.ops] }
}

/** True for a sent quotation whose last valid day has passed (never stored). */
export function isQuotationExpired(
  order: Pick<SalesOrderRow, 'kind' | 'status' | 'valid_until'>,
  todayIso: string,
): boolean {
  return (
    order.kind === 'quotation' &&
    order.status === 'sent' &&
    order.valid_until !== null &&
    order.valid_until < todayIso
  )
}

/** Quantity already on finalized bills, by order line id. */
export type InvoicedByLine = Readonly<Record<string, Milli>>

export type OrderProgress = 'none' | 'part' | 'full' | 'over'

/** How far a document has been billed: summed from the bills, never stored. */
export function orderProgress(
  lines: readonly Pick<SalesOrderLineRow, 'id' | 'qty_milli'>[],
  invoiced: InvoicedByLine,
): { orderedMilli: Milli; invoicedMilli: Milli; progress: OrderProgress } {
  let orderedMilli = 0
  let invoicedMilli = 0
  let over = false
  let short = false
  for (const l of lines) {
    const done = invoiced[l.id] ?? 0
    orderedMilli += l.qty_milli
    invoicedMilli += done
    if (done > l.qty_milli) over = true
    if (done < l.qty_milli) short = true
  }
  const progress: OrderProgress =
    invoicedMilli === 0 ? 'none' : short ? 'part' : over ? 'over' : 'full'
  return { orderedMilli, invoicedMilli, progress }
}

/**
 * The lines still to bill, ready for buildInvoice: what is left of each line
 * at the agreed price, with the line's discount in proportion.
 */
export function invoiceLinesFromOrder(
  lines: readonly SalesOrderLineRow[],
  invoiced: InvoicedByLine = {},
): DocLineInput[] {
  const out: DocLineInput[] = []
  for (const l of [...lines].sort((a, b) => a.line_no - b.line_no)) {
    const left = l.qty_milli - (invoiced[l.id] ?? 0)
    if (left <= 0) continue
    out.push({
      productId: l.product_id,
      description: l.description,
      qtyMilli: left,
      unitPricePaisa: l.unit_price_paisa,
      discountPaisa:
        left === l.qty_milli ? l.discount_paisa : mulDivRound(l.discount_paisa, left, l.qty_milli),
      taxRateId: l.tax_rate_id,
      taxRateBp: l.tax_rate_bp,
      priceIncludesTax: l.price_includes_tax,
      salesOrderLineId: l.id,
    })
  }
  return out
}
